<template>
  <p class="db-form-legend">
    <span
      class="db-input-required-indicator"
      aria-hidden="true"
      >*</span
    >
    {{ $t('Fields with an asterisk are required') }}
  </p>

  <form
    novalidate
    class="db-funnel-checkout-form"
    v-on:submit.stop.prevent="tryCheckout"
  >
    <!-- information bloc -->
    <fieldset>
      <DbCardGroup class="db-group-information">
        <template v-slot:title>
          <legend>
            {{ $t('Contact information') }}
          </legend>
        </template>
        <div class="db-group-information-content">
          <div>
            <DbInput
              v-model="user.firstName"
              :inputAttrs="{
                required: true,
                autocomplete: 'given-name',
              }"
            >
              {{ $t('First Name') }}
              <template
                v-if="v$.fieldFirstName.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldFirstName.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>

          <div>
            <DbInput
              v-model="user.lastName"
              :inputAttrs="{
                required: true,
                autocomplete: 'family-name',
              }"
            >
              {{ $t('Last Name') }}
              <template
                v-if="v$.fieldLastName.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldLastName.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>

          <div>
            <DbInput
              v-model="user.email"
              :inputAttrs="{
                required: true,
                type: 'email',
                autocomplete: 'email',
              }"
            >
              {{ $t('Email_Address') }}
              <template v-slot:hint> {{ $t('Ex: {x}', { x: 'jane.doe@contact.com' }) }} </template>
              <template
                v-if="v$.fieldEmail.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldEmail.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>

          <div>
            <DbInput
              v-model="user.phone"
              :inputAttrs="{
                required: true,
                type: 'tel',
                maxlength: '12',
                inputmode: 'tel',
                autocomplete: 'tel',
              }"
            >
              {{ $t('Phone Number') }}
              <template v-slot:hint>
                {{ $t('Phone number with country code. Ex: +33 6 77 82 43 59') }}
              </template>
              <template
                v-if="v$.fieldPhone.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldPhone.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>

          <div>
            <DbInput
              v-model="user.company"
              :inputAttrs="{
                autocomplete: 'organization',
              }"
            >
              {{ $t('Company') }}
              <template
                v-if="v$.fieldCompany.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldCompany.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>

          <div>
            <DbInput
              v-model="options.eventName"
              :inputAttrs="{
                required: true,
                autocomplete: 'off',
              }"
            >
              {{ $t('Event Name') }}
              <template
                v-if="v$.fieldEventName.$error"
                v-slot:error
              >
                <div
                  v-for="error in v$.fieldEventName.$errors"
                  :key="error.$uid"
                >
                  {{ error.$message }}
                </div>
              </template>
            </DbInput>
          </div>
        </div>
      </DbCardGroup>
    </fieldset>

    <!-- !information bloc -->

    <!-- guestrooms bloc -->
    <fieldset v-if="configStore.funnel.showGuestrooms">
      <DbCardGroup class="db-card-group db-group-guestrooms">
        <template v-slot:title>
          <legend>
            {{ $t('Guestrooms') }}
          </legend>
        </template>
        <div class="db-group-guestrooms-content">
          <p>{{ $t('Your request will be forwarded after your booking') }}</p>
          <p class="db-text-small db-group-guestrooms-note">
            {{ $t('Note: These are not guaranteed.') }}
          </p>

          <DbCheckbox
            v-model="options.needGuestrooms"
            class="db-group-guestrooms-cb"
            :inputAttrs="{
              name: 'guestrooms-cb',
              'aria-controls': 'db-field-guestrooms-quantity',
            }"
          >
            {{ $t('Do you need guestrooms for your attendees?') }}
          </DbCheckbox>
          <!-- TODO: temporary while waiting for DbCheckbox to have a slot for errors -->
          <div class="db-tw-text-error-message db-tw-mt-2">
            <div
              v-for="error in v$.fieldNeedGuestrooms.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>

          <DbInput
            v-if="options.needGuestrooms"
            id="db-field-guestrooms-quantity"
            :modelValue="String(options.guestroomsQuantity)"
            class="db-group-guestrooms-quantity"
            :inputAttrs="{
              autocomplete: 'organization',
              type: 'number',
              min: 0,
            }"
            v-on:update:model-value="options.guestroomsQuantity = Number($event)"
          >
            {{ $t('# of Rooms') }}
            <template
              v-if="v$.fieldGuestroomsQuantity.$error"
              v-slot:error
            >
              <div
                v-for="error in v$.fieldGuestroomsQuantity.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </template>
          </DbInput>
        </div>
      </DbCardGroup>
    </fieldset>
    <!-- !guestrooms bloc -->

    <!-- prepare your event bloc -->
    <fieldset>
      <DbCardGroup class="db-card-group db-group-prepare">
        <template v-slot:title>
          <legend>
            {{ $t('Prepare your event') }}
          </legend>
        </template>
        <div class="db-tw-pt-3 2md:db-tw-pt-2">
          <p class="db-bold db-group-prepare-title">
            {{ $t('A special request to make to the Hotel?') }}
          </p>
          <p class="db-group-prepare-text">
            {{ $t('Your request will be forwarded after your booking') }}
          </p>
          <p class="db-text-small db-group-prepare-note">
            {{ $t('Note: These are not guaranteed.') }}
          </p>
          <DbTextarea
            v-model="options.comments"
            class="db-group-prepare-comment"
          >
            {{ $t('Additional comments') }}
            <template
              v-if="v$.fieldComments.$error"
              v-slot:error
            >
              <div
                v-for="error in v$.fieldComments.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </template>
          </DbTextarea>
        </div>
      </DbCardGroup>
    </fieldset>
    <!-- !prepare your event bloc -->

    <!-- payment bloc -->
    <fieldset>
      <DbCardGroup class="db-card-group db-group-payment">
        <template v-slot:title>
          <legend>
            {{ $t('Payment details') }}
          </legend>
        </template>
        <div class="2md:db-tw-pt-4">
          <p class="db-group-payment-text">
            <span v-if="general.payPercentage === 0">{{
              $t(
                'Your reservation will be confirmed after clicking Book now. The payment will be requested at the property.',
              )
            }}</span>
            <span v-else-if="general.payPercentage === 100">{{
              $t(
                'You will be redirected in order to finish payment once the Pay Now button is clicked.',
              )
            }}</span>
            <span v-else>{{
              $t(
                'You’ll be paying only a {x}% of the reservation now and the remaining will be paid on property upon arrival. In order to finish this first payment you’ll be redirected once the Pay Now button is clicked.',
                { x: general.payPercentage },
              )
            }}</span>
          </p>

          <!-- Display the deposit details table on Desktop -->
          <DbDepositTable v-if="!isMobile" />

          <!-- Terms and conditions -->
          <div
            :class="[
              'db-tw-my-4 db-group-terms',
              {
                'db-has-error': v$.fieldTerms.$error,
              },
            ]"
          >
            <div class="db-checkbox-item">
              <DbCheckbox
                v-model="formData.terms"
                :inputAttrs="{
                  value: 'terms',
                  name: 'terms',
                }"
              >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="funnel.termsAndConditions" />
                <span class="db-input-required-indicator">*</span>
              </DbCheckbox>
              <!-- TODO: temporary while waiting for DbCheckbox to have a slot for errors -->
              <div class="db-tw-text-error-message db-tw-mt-2">
                <DbRfpErrors
                  v-if="v$.fieldTerms.$error"
                  :errors="v$.fieldTerms.$errors"
                />
              </div>
            </div>
          </div>

          <!-- @slot On mobile, there are some cart data displayed here -->
          <slot />

          <div
            v-if="!!whiteListLocationIDMessage"
            class="db-tw-text-error-message db-tw-mt-2 db-tw-text-center db-tw-font-bold"
          >
            {{ whiteListLocationIDMessage }}
          </div>
          <button
            type="submit"
            class="db-button-primary db-button-large db-uppercase db-group-payment-submit"
          >
            <span v-if="general.payPercentage !== 0">{{ $t('Pay now') }}</span>
            <span v-else>{{ $t('Book now') }}</span>
          </button>
          <div
            class="db-tw-text-error-message db-tw-mt-2 db-tw-text-center db-retry-error db-tw-hidden"
          >
            {{ $t('An error occurred with the payment provider.') }}<br />
            {{ $t('Please retry to finalize your booking.') }}
          </div>
          <div
            class="db-tw-text-error-message db-tw-mt-2 db-tw-text-center db-retry-error-booked db-tw-hidden"
          >
            {{ $t('Sorry, your selection is no longer available.') }}<br />
            {{ $t('Please try again.') }}
          </div>
        </div>
      </DbCardGroup>
    </fieldset>
    <!-- !payment your event bloc -->
  </form>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, email, numeric } from '@/utils/i18n-validators'
import { useScrollToElement } from '@/features/RfpForm/composables/useRfpForm'
import { validCheckbox } from '@/utils/i18n-validators'
import { useBreakpoints } from '@vueuse/core'

import { useConfigurations } from '@/stores/configurations'
import { useUserStore } from '@/stores/user'
import { useCartStore } from '@/stores/cart'
import { storeToRefs } from 'pinia'

const emit = defineEmits<{
  /** emited when form is valid to trigger loading and redirection */
  (e: 'checkout'): void
}>()

const configStore = useConfigurations()
const { funnel, general } = storeToRefs(configStore)

const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const cartStore = useCartStore()
const { options, whiteListLocationIDMessage } = storeToRefs(cartStore)

const breakpoints = useBreakpoints({ desktop: 992 })
const isMobile = breakpoints.smaller('desktop')

/** Form v-model local to this component */
const formData = reactive({
  terms: false,
})

/** Form validation - state */
const state = computed(() => {
  return {
    fieldFirstName: user.value.firstName,
    fieldLastName: user.value.lastName,
    fieldEmail: user.value.email,
    fieldPhone: user.value.phone,
    fieldCompany: user.value.company,
    fieldNeedGuestrooms: options.value.needGuestrooms,
    fieldGuestroomsQuantity: options.value.guestroomsQuantity,
    fieldComments: options.value.comments,
    fieldTerms: formData.terms,
    fieldEventName: options.value.eventName,
  }
})

/** Form validation - rules */
const rules = computed(() => {
  return {
    fieldFirstName: { required },
    fieldLastName: { required },
    fieldEmail: { required, email },
    fieldPhone: { required },
    fieldCompany: {},
    fieldNeedGuestrooms: {},
    fieldGuestroomsQuantity: { numeric },
    fieldComments: {},
    fieldTerms: { validCheckbox },
    fieldEventName: { required },
  }
})

const v$ = useVuelidate(rules, state, { $autoDirty: true })

/** Submit checkout form */
async function tryCheckout() {
  v$.value.$touch()

  const isFormCorrect = await v$.value.$validate()
  if (isFormCorrect) {
    // TODO: xo7: triggers the loading screen in a
    emit('checkout')
  } else {
    /* Validation is not ok - Scroll To error */
    useScrollToElement('.db-has-error')
  }
}
</script>

<style>
/* To align and have correct style on check terms */
.db-group-terms .db-checkbox-item label span p {
  display: inline;
}

.db-group-terms .db-checkbox-item .db-input-required-indicator {
  margin: 0 var(--db-spacer-3);
}

/* When terms is on error, to apply red color to check input */
.db-group-terms.db-has-error .db-checkbox-item label span p {
  color: var(--db-color-text-input-field-label-error);
}
</style>
